import { formatMonthYear, getDateLabelForContent } from '@/utils/date';
import { tForContent } from '@/utils/locale';
import { Experience, TotalExperience } from '@/types/experience.types'
import type { Candidate } from '@/models/job';
import { normalizeExperience } from '@/utils/experienceNormalizer';

/**
 * Helper function to get value from enhancedData with type assertion
 * Checks both extracted_info and direct access
 */
export const getEnhancedDataValue = <T = unknown>(
  enhancedData: Record<string, unknown> | null | undefined,
  key: string
): T | null => {
  if (!enhancedData) return null;

  // Check inside extracted_info first
  if (enhancedData.extracted_info && typeof enhancedData.extracted_info === 'object') {
    const extractedInfo = enhancedData.extracted_info as Record<string, unknown>;
    if (extractedInfo[key] !== undefined) {
      return extractedInfo[key] as T;
    }
  }

  // Then try direct access
  if (enhancedData[key] !== undefined) {
    return enhancedData[key] as T;
  }

  return null;
};

/**
 * Parse enhancedData if it's a string
 */
export const parseEnhancedData = (
  enhancedData: unknown
): Record<string, unknown> | null | undefined => {
  if (typeof enhancedData === 'string') {
    try {
      return JSON.parse(enhancedData);
    } catch (e) {
      console.error('Failed to parse enhancedData string:', e);
      return null;
    }
  }
  return enhancedData as Record<string, unknown> | null | undefined;
};

/**
 * Extract candidate data from enhancedData
 */
export const extractCandidateData = (candidate: Candidate) => {
  const parsedEnhancedData = parseEnhancedData(candidate?.enhancedData);
  const socialLinks = getEnhancedDataValue<unknown[]>(parsedEnhancedData, 'social_links');
  const getSocialValue = (key: string): string | undefined => {
    if (!Array.isArray(socialLinks)) return undefined;
    const link = socialLinks.find(
      (entry) =>
        typeof entry === 'object'
        && entry !== null
        && (entry as Record<string, unknown>).type === key
        && typeof (entry as Record<string, unknown>).link === 'string'
    ) as Record<string, unknown> | undefined;
    return (link?.link as string | undefined) ?? undefined;
  };

  // Get total_experience (new format)
  const totalExperience = getEnhancedDataValue<TotalExperience>(parsedEnhancedData, 'total_experience');

  const yearsOfExperience = totalExperience?.years ?? getEnhancedDataValue<number>(parsedEnhancedData, 'years_of_experience') ?? 0;

  return {
    technicalSkills: getEnhancedDataValue<string[]>(parsedEnhancedData, 'technical_skills') || [],
    experience: ( getEnhancedDataValue<any[]>(parsedEnhancedData, 'experience') || []).map(normalizeExperience),    
    education: getEnhancedDataValue<string[]>(parsedEnhancedData, 'education') || [],
    languages: getEnhancedDataValue<string[]>(parsedEnhancedData, 'languages') || [],
    strengths: getEnhancedDataValue<string[]>(parsedEnhancedData, 'strengths') || [],
    weaknesses: getEnhancedDataValue<string[]>(parsedEnhancedData, 'weaknesses') || [],
    experienceSummary: totalExperience
    ? {
        years: totalExperience.years,
        months: totalExperience.months,
      }
    : yearsOfExperience != null
      ? {
          years: yearsOfExperience,
          months: 0,
        }
      : null,
    email: getEnhancedDataValue<string>(parsedEnhancedData, 'email'),
    firstName: getEnhancedDataValue<string>(parsedEnhancedData, 'first_name'),
    lastName: getEnhancedDataValue<string>(parsedEnhancedData, 'last_name'),
    title: getEnhancedDataValue<string>(parsedEnhancedData, 'title'),
    sourceLocale: getEnhancedDataValue<string>(parsedEnhancedData, 'source_locale'),
    linkedin: getSocialValue('linkedin') ?? candidate?.talentLinkedinUrl,
    github: getSocialValue('github'),
    kaggle: getSocialValue('kaggle'),
    researchGate: getSocialValue('researchgate'),
    leetCode: getSocialValue('leetcode'),
    googleScholar: getSocialValue('google_scholar'),
    blog: getSocialValue('blog'),
  };
};

/** Format years + months duration using CV source language. */
export function formatExperienceDurationParts(
  years: number,
  months: number,
  contentLocale?: string | null,
): string {
  const parts: string[] = [];
  if (years > 0) {
    parts.push(
      tForContent('job:candidate_info.years_count', contentLocale, { count: years }),
    );
  }
  if (months > 0) {
    parts.push(
      tForContent('common:dates.months_count', contentLocale, { count: months }),
    );
  }
  return parts.join(' ');
}

/**
 * Format a decimal years-of-experience value (e.g. 0.8333) as a "X ans Y mois"
 * label, reusing the exact wording of the candidate experience display. Rounds
 * to whole months first so 0.8333 → "10 mois" instead of "0.83 an", and avoids
 * a 12-month rollover. Returns '' when there is no experience (caller decides
 * the empty fallback, e.g. a dash).
 */
export function formatYearsOfExperience(
  totalYoe?: number | null,
  contentLocale?: string | null,
): string {
  const totalMonths = Math.max(0, Math.round((totalYoe ?? 0) * 12));
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  return formatExperienceDurationParts(years, months, contentLocale);
}

export function formatExperience(
  exp: Experience,
  contentLocale?: string | null,
): string {
  // Legacy → return as-is
  if (exp.isLegacy && exp.legacyText) {
    return exp.legacyText;
  }

  const role =
    exp.role
    || tForContent('job:candidate_info.experience_unknown_role', contentLocale);
  const company =
    exp.company
    || tForContent('job:candidate_info.experience_unknown_company', contentLocale);

  const years = exp.years ?? 0;
  const months = exp.months ?? 0;

  const durationText = formatExperienceDurationParts(years, months, contentLocale);
  const duration = durationText ? ` - ${durationText}` : '';

  const formatExperienceDate = (date?: string | Date) =>
    date ? formatMonthYear(date, contentLocale) : '';

  const start = formatExperienceDate(exp.startDate);
  const end = exp.isOngoing
    ? getDateLabelForContent('present', contentLocale)
    : exp.endDate
      ? formatExperienceDate(exp.endDate)
      : '';

  const dateRange =
    start || end ? ` (${start}${start && end ? ' – ' : ''}${end})` : '';

  return tForContent('job:candidate_info.experience_entry', contentLocale, {
    role,
    company,
    dateRange,
    duration,
  });
}
